import React from 'react'
import {
  Stack,
  Heading,
  Text,
  Card,
  Button,
  Flex,
  Icon,
  Link,
  CardProps,
  LinkButton,
  useToast,
  Box,
} from '@thirstycamel/ui'
import type { StoreEntity } from '@ts/core/src/modules/store/store.entity'

import { useActions, useStore } from '../../store/hooks'
import StoreHours from '../StoreHours'
import { useRouter } from 'next/router'

/** TODO: Be more creative when naming components */
const TextAndIcon = ({ text, icon, ...restProps }) => (
  <Text fontSize="xs" fontWeight="medium" textAlign="center" {...restProps}>
    <Icon name={icon} fontSize="xs" mr={1} />
    {text}
  </Text>
)

///////////////////////////////////////////////////////////////////////////////////////////////////

export interface StoreCardProps extends CardProps {
  store: StoreEntity
}

const StoreCard = ({ store, ...restProps }: StoreCardProps) => {
  const toast = useToast()

  const router = useRouter()
  const { selectStore } = useActions(a => a.store)
  const selectedStore = useStore(a => a.store.selectedStore)
  const { fetchProductLists } = useActions(actions => actions.navigation)

  const GOOGLE_MAPS_LINK = `https://www.google.com/maps/dir/?api=1&${
    store?.placeId ? `destination_place_id=${store?.placeId}&` : ''
  }destination=${store?.name}, ${store?.address} ${store?.postcode}`

  const handleSelect = async () => {
    await selectStore(store)
    fetchProductLists(store?.region)

    toast({
      title: 'Store selected!',
      status: 'success',
    })

    if (router.query?.backTo && router.query?.backToAs) {
      router.push(router.query.backTo.toString(), router.query.backToAs.toString())
    }
  }

  return (
    <Card borderRadius="lg" overflow="hidden" display="flex" flexDir="column" {...restProps}>
      <Stack
        isInline
        // align="center"
        justify="space-between"
        spacing={4}
        bg="green.100"
        px={[4, 6, 6]}
        py={[4, , 4]}
        borderBottom="1px"
        borderColor="green.400"
      >
        <Stack spacing={1}>
          <Link href="/stores/[slug]" hrefAs={`/stores/${store.slug}`}>
            <Heading color="pink.500" fontSize="sm" textTransform="uppercase">
              {store.name}
            </Heading>
          </Link>

          <Stack isInline spacing={3} flexWrap="wrap">
            {store?.isDeliveryReady && <TextAndIcon text="Delivery" icon="truck" />}
            {store?.isPickupReady && <TextAndIcon text="Pickup" icon="briefcase" />}
            {store?.isLoyaltyRewards && <TextAndIcon text="Loyalty Rewards" icon="check-shield" />}
          </Stack>
        </Stack>

        {store.distance && (
          <Stack isInline spacing={1} align="center" color="pink.500" h="100%" pt="2px">
            <Icon name="place" />
            <Text fontSize="sm" fontWeight="bold">
              {store.distance}km
            </Text>
          </Stack>
        )}
      </Stack>

      <Stack spacing={6} px={[4, 6, 6]} pt={6} pb={[6, , 8]} flexGrow={1} align="flex-start">
        <Flex direction={['column', , 'row']} w="100%">
          <Stack spacing={3} pr={[, , 3]}>
            <Heading fontSize={['xl', '2xl']}>{store.address}</Heading>

            {store.phoneNumber && (
              <Link
                href={`tel:${store.phoneNumber}`}
                isExternal
                fontFamily="heading"
                fontSize="xl"
                color="pink.500"
              >
                {store.phoneNumber}
              </Link>
            )}

            {store.phoneNumber && (
              <Text fontSize="xl" textDecor="none" fontFamily="heading">
                Liquor License No. {store?.liquorLicenseNumber}
              </Text>
            )}

            {/* <Stack>
              {features?.map(feature => (
                <Stack key={feature} align="center" isInline>
                  <Icon name="info" fontSize="sm" color="blackAlpha.700" />
                  <Text fontSize="sm" fontWeight="medium" color="blackAlpha.600">
                    {feature}
                  </Text>
                </Stack>
              ))}
            </Stack> */}
          </Stack>
        </Flex>

        <Box flex={1}>
          {store.hours ? (
            <StoreHours hours={store.hours} />
          ) : (
            <Text>Contact store for opening hours.</Text>
          )}
        </Box>

        <Stack w="100%" spacing={4}>
          <LinkButton
            href={GOOGLE_MAPS_LINK}
            isExternal
            variantColor="pink"
            isRound
            w="100%"
            rightIcon="external-link"
          >
            Get directions
          </LinkButton>

          <Button
            variant="outline"
            variantColor="pink"
            w="100%"
            isRound
            onClick={handleSelect}
            isDisabled={store.slug === selectedStore?.slug}
            rightIcon={store.slug !== selectedStore?.slug ? 'place' : 'check'}
          >
            {store.slug === selectedStore?.slug ? 'Store selected' : 'Set as my store'}
          </Button>
        </Stack>
      </Stack>
    </Card>
  )
}

export default StoreCard
